import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Checkbox } from "../ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Badge } from "../ui/badge";
import {
  Play, CheckCircle2, RotateCcw, Send, Radio, Clock, MapPin,
  AlertTriangle, Sparkles, Trash2, MessageSquare, FastForward,
  Undo2, History, X, ListChecks,
} from "lucide-react";
import { toast } from "sonner";
import WeatherWidget from "../WeatherWidget";

/** Parse "HH:MM" → minutes-from-midnight (returns 0 on bad input) */
function timeToMin(t) {
  if (!t || typeof t !== "string") return 0;
  const [h, m] = t.split(":").map(Number);
  return (isFinite(h) ? h : 0) * 60 + (isFinite(m) ? m : 0);
}

function nowMinLocal() {
  const d = new Date();
  return d.getHours() * 60 + d.getMinutes();
}

function formatDuration(mins) {
  if (mins < 0) mins = 0;
  const h = Math.floor(mins / 60);
  const m = Math.floor(mins % 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

function humanTime(t) {
  if (!t) return "";
  const [h, m] = t.split(":").map(Number);
  const d = new Date();
  d.setHours(h || 0, m || 0, 0, 0);
  return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}

function relativeTime(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  const diff = Math.round((Date.now() - then) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return new Date(iso).toLocaleString();
}

export default function LiveTab({ eventId, members, canEdit, canPost = false, myRole }) {
  const { user } = useAuth();
  const myUserId = user?.user_id;
  const [blocks, setBlocks] = useState([]);
  const [updates, setUpdates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [posting, setPosting] = useState(false);
  const [audience, setAudience] = useState("all");
  const [message, setMessage] = useState("");
  const messageRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const [{ data: t }, { data: u }] = await Promise.all([
        api.get(`/events/${eventId}/timeline`),
        api.get(`/events/${eventId}/updates`),
      ]);
      const sorted = [...t].sort((a, b) => (a.time || "").localeCompare(b.time || ""));
      setBlocks(sorted);
      setUpdates(u);
    } catch (e) {
      toast.error("Could not load live view");
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => { load(); }, [load]);
  // Poll every 15s to keep the room in sync
  useEffect(() => {
    const id = setInterval(load, 15000);
    return () => clearInterval(id);
  }, [load]);

  const vendorMembers = useMemo(
    () => members.filter(m => m.role === "vendor"),
    [members],
  );

  const { current, nextUp, completed } = useMemo(() => {
    const nowMin = nowMinLocal();
    const anyStarted = blocks.find(b => b.started_at && !b.completed_at);
    let cur = anyStarted || null;
    if (!cur) {
      // Auto-highlight: the block whose window covers "now" (time..time+duration)
      cur = blocks.find(b => {
        const start = timeToMin(b.time);
        const end = start + (b.duration_min || 0);
        return nowMin >= start && nowMin < end && !b.completed_at;
      }) || null;
    }
    const completedList = blocks.filter(b => b.completed_at);
    const upcoming = blocks.filter(b => !b.completed_at && b !== cur);
    return { current: cur, nextUp: upcoming.slice(0, 3), completed: completedList };
  }, [blocks]);

  const doAction = async (blockId, action) => {
    try {
      await api.post(`/events/${eventId}/timeline/${blockId}/live`, { action });
      await load();
      toast.success(
        action === "start" ? "Started" :
        action === "complete" ? "Marked complete" :
        "Reset"
      );
    } catch (e) {
      toast.error("Could not update block");
    }
  };

  const saveNote = async (blockId, note) => {
    try {
      await api.post(`/events/${eventId}/timeline/${blockId}/live`, { live_note: note });
      await load();
    } catch (e) {
      toast.error("Could not save note");
    }
  };

  const shiftAfter = async ({ blockId, deltaMin, broadcast }) => {
    try {
      const body = {
        from_block_id: blockId,
        delta_min: deltaMin,
        include_anchor: broadcast?.includeAnchor !== undefined ? broadcast.includeAnchor : true,
      };
      if (broadcast?.message) {
        body.broadcast_message = broadcast.message;
        body.broadcast_audience = broadcast.audience || "all";
      }
      const { data } = await api.post(`/events/${eventId}/timeline/shift-after`, body);
      await load();
      toast.success(
        `Shifted ${data.shifted} block${data.shifted === 1 ? "" : "s"} by ${deltaMin > 0 ? "+" : ""}${deltaMin}m`
        + (data.update ? " · update broadcast" : "")
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not shift timeline");
      throw e;
    }
  };

  const doRewind = async ({ blockId, message, audience }) => {
    try {
      const body = { action: "rewind" };
      if (message) {
        body.broadcast_message = message;
        body.broadcast_audience = audience || "all";
      }
      await api.post(`/events/${eventId}/timeline/${blockId}/live`, body);
      await load();
      toast.success(message ? "Rewound — update broadcast" : "Rewound");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not rewind");
      throw e;
    }
  };

  // Audit trail modal state (loaded lazily)
  const [auditOpen, setAuditOpen] = useState(false);
  const [audit, setAudit] = useState(null);
  const [auditLoading, setAuditLoading] = useState(false);
  const openAudit = async () => {
    setAuditOpen(true);
    if (audit) return;
    setAuditLoading(true);
    try {
      const { data } = await api.get(`/events/${eventId}/audit?limit=200`);
      setAudit(data);
    } catch (e) {
      toast.error("Could not load activity log");
      setAuditOpen(false);
    } finally {
      setAuditLoading(false);
    }
  };
  const refreshAudit = async () => {
    setAuditLoading(true);
    try {
      const { data } = await api.get(`/events/${eventId}/audit?limit=200`);
      setAudit(data);
    } catch (e) { console.error("audit refresh failed:", e); toast.error("Could not refresh activity log"); }
    finally { setAuditLoading(false); }
  };

  const postUpdate = async () => {
    const msg = message.trim();
    if (!msg) { toast.error("Type a message first"); return; }
    setPosting(true);
    try {
      await api.post(`/events/${eventId}/updates`, { message: msg, audience, kind: "announcement" });
      setMessage("");
      await load();
      toast.success("Update sent");
      messageRef.current?.focus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send update");
    } finally {
      setPosting(false);
    }
  };

  const deleteUpdate = async (id) => {
    if (!confirm("Delete this update?")) return;
    try {
      await api.delete(`/events/${eventId}/updates/${id}`);
      setUpdates(u => u.filter(x => x.id !== id));
    } catch (e) {
      toast.error("Could not delete");
    }
  };

  if (loading) return <div className="text-muted text-sm">Loading live view…</div>;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-testid="live-tab-root">
      {/* LEFT: Run of Show */}
      <div className="lg:col-span-2 space-y-8">
        <div className="flex items-center gap-3">
          <div className="w-2 h-2 rounded-full bg-[#EF4444] live-pulse" aria-hidden />
          <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#EF4444]">MOD.LIVE / RUN_OF_SHOW</div>
          {canEdit && (
            <button
              type="button"
              onClick={openAudit}
              data-testid="live-audit-open"
              className="ml-auto inline-flex items-center gap-1.5 text-xs text-muted hover:text-app border border-hair hover:border-[#0A0A0A] rounded-sm px-2.5 py-1 transition-colors font-mono uppercase tracking-widest"
            >
              <History size={11} strokeWidth={1.5}/> Activity
            </button>
          )}
        </div>

        {/* NOW card */}
        <NowCard block={current} canEdit={canEdit} onAction={doAction} onNote={saveNote} onShift={shiftAfter} onRewind={doRewind} />

        {/* NEXT UP */}
        <section className="space-y-3" data-testid="live-next-up">
          <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted flex items-center gap-2"><Clock size={11} strokeWidth={1.5}/> MOD.LIVE / NEXT_UP</div>
          {nextUp.length === 0 ? (
            <p className="text-sm text-muted font-light">Nothing else on the schedule.</p>
          ) : (
            <div className="space-y-2">
              {nextUp.map(b => (
                <UpcomingRow key={b.id} block={b} canEdit={canEdit} onStart={() => doAction(b.id, "start")} />
              ))}
            </div>
          )}
        </section>

        {/* COMPLETED */}
        {completed.length > 0 && (
          <section className="space-y-3">
            <div className="eyebrow">Done ({completed.length})</div>
            <div className="space-y-2">
              {completed.map(b => (
                <CompletedRow key={b.id} block={b} canEdit={canEdit} onReset={() => doAction(b.id, "reset")} />
              ))}
            </div>
          </section>
        )}
      </div>

      {/* RIGHT: Live updates feed */}
      <aside className="lg:col-span-1">
        <div className="sticky top-20 space-y-4">
          <WeatherWidget eventId={eventId} variant="now" canEdit={canEdit} />
          <SetupProgressWidget eventId={eventId} />
          <div className="flex items-center gap-2">
            <Radio size={13} strokeWidth={1.5} className="text-app" />
            <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted">MOD.LIVE / UPDATES_FEED</div>
          </div>

          {canPost && (
            <div className="border border-hair rounded-sm p-3 bg-surface space-y-2" data-testid="update-composer">
              <Textarea
                ref={messageRef}
                data-testid="update-composer-message"
                placeholder={myRole === "vendor"
                  ? "Send a status update to the planner — e.g. arrived, setup complete, running 10 min late."
                  : "Update the room — ceremony pushed 15 min, cocktail hour ready, etc."}
                value={message}
                onChange={e => setMessage(e.target.value)}
                rows={3}
                className="text-sm"
              />
              <div className="flex gap-2 items-center">
                {myRole === "vendor" ? (
                  <div
                    data-testid="update-composer-vendor-hint"
                    className="flex-1 text-xs text-muted italic px-2"
                  >
                    Posts as a vendor status to the planner + team.
                  </div>
                ) : (
                  <Select value={audience} onValueChange={setAudience}>
                    <SelectTrigger data-testid="update-composer-audience" className="h-9 rounded-sm text-xs flex-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Everyone</SelectItem>
                      <SelectItem value="vendors">Vendors only</SelectItem>
                      <SelectItem value="clients">Clients only</SelectItem>
                      {vendorMembers.length > 0 && (
                        <>
                          <div className="px-2 pt-2 pb-1 text-[10px] uppercase tracking-widest text-muted">Specific vendor</div>
                          {vendorMembers.map(v => (
                            <SelectItem key={v.id} value={`vendor:${v.id}`}>
                              {v.name || v.email}
                            </SelectItem>
                          ))}
                        </>
                      )}
                    </SelectContent>
                  </Select>
                )}
                <Button
                  onClick={postUpdate}
                  disabled={posting || !message.trim()}
                  data-testid="update-composer-send"
                  className="bg-app hover:bg-[#000000] rounded-sm text-xs h-9"
                >
                  <Send size={12} strokeWidth={1.5} className="mr-1.5" />
                  {posting ? "Sending…" : "Send"}
                </Button>
              </div>
            </div>
          )}

          <div className="space-y-3 max-h-[calc(100vh-320px)] overflow-y-auto pr-1" data-testid="updates-feed">
            {updates.length === 0 ? (
              <p className="text-sm text-muted font-light">
                No updates yet.{canPost ? " Post the first one to keep the room in sync." : ""}
              </p>
            ) : (
              updates.map(u => {
                const canDeleteThis = canEdit || (u.author_user_id && u.author_user_id === myUserId);
                return (
                  <UpdateCard key={u.id} update={u} canDelete={canDeleteThis} onDelete={() => deleteUpdate(u.id)} />
                );
              })
            )}
          </div>
        </div>
      </aside>

      <AuditModal
        open={auditOpen}
        loading={auditLoading}
        entries={audit}
        onClose={() => setAuditOpen(false)}
        onRefresh={refreshAudit}
      />
    </div>
  );
}

/* --- Sub-components --- */

function NowCard({ block, canEdit, onAction, onNote, onShift, onRewind }) {
  const [note, setNote] = useState(block?.live_note || "");
  const [tick, setTick] = useState(0);
  const [shiftOpen, setShiftOpen] = useState(false);
  const [shiftDelta, setShiftDelta] = useState(15);
  const [broadcast, setBroadcast] = useState(true);
  const [shiftMsg, setShiftMsg] = useState("");
  const [shiftMsgTouched, setShiftMsgTouched] = useState(false);
  const [shiftIncludeAnchor, setShiftIncludeAnchor] = useState(true);
  const [shiftAudience, setShiftAudience] = useState("all");
  const [applying, setApplying] = useState(false);
  const [rewindOpen, setRewindOpen] = useState(false);
  const [rewindMsg, setRewindMsg] = useState("");
  const [rewindAudience, setRewindAudience] = useState("all");
  const [rewindBroadcast, setRewindBroadcast] = useState(true);
  const [rewinding, setRewinding] = useState(false);

  useEffect(() => setNote(block?.live_note || ""), [block?.id, block?.live_note]);
  useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), 30000);
    return () => clearInterval(id);
  }, []);
  useEffect(() => {
    setShiftOpen(false);
    setRewindOpen(false);
    setShiftMsgTouched(false);
  }, [block?.id]);

  const started = !!block?.started_at;
  const plannedMin = block?.duration_min || 0;
  const elapsedMin = started
    ? Math.max(0, Math.floor((Date.now() - new Date(block.started_at).getTime()) / 60000))
    : 0;
  const scheduledStart = block ? timeToMin(block.time) : 0;
  const nowMin = nowMinLocal();
  const startDelay = !block ? 0 : (started
    ? Math.floor((new Date(block.started_at).getHours() * 60 + new Date(block.started_at).getMinutes()) - scheduledStart)
    : nowMin - scheduledStart);
  const overBy = started ? Math.max(0, elapsedMin - plannedMin) : 0;
  const isDelayed = !block ? false : (started ? overBy > 0 : startDelay > 5);
  const suggestedDelta = isDelayed ? Math.min(120, started ? overBy : Math.max(1, startDelay)) : 15;
  // Update suggested delta whenever we newly detect a delay (only on block change)
  useEffect(() => { setShiftDelta(suggestedDelta > 0 ? suggestedDelta : 15); }, [block?.id, isDelayed, suggestedDelta]);
  // Auto-compose the broadcast message when opening the panel — but only until the
  // planner manually edits it, so preset chip clicks don't clobber their wording.
  useEffect(() => {
    if (shiftOpen && !shiftMsgTouched && block) {
      const dir = shiftDelta >= 0 ? "shifted" : "moved earlier by";
      const abs = Math.abs(shiftDelta);
      setShiftMsg(`Timeline update: "${block.title}" and everything after ${dir} ${abs} min. Please adjust accordingly.`);
    }
  }, [shiftOpen, shiftDelta, block, shiftMsgTouched]);
  // Silence unused warning for `tick` — it's only here to force re-render
  void tick;

  if (!block) {
    return (
      <div className="border border-hair rounded-sm p-8 bg-surface text-center" data-testid="live-now-empty">
        <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted mb-3">MOD.LIVE / STATE: IDLE</div>
        <Sparkles size={20} strokeWidth={1.2} className="text-muted mx-auto" />
        <div className="mt-3 font-display text-xl md:text-2xl font-bold text-app tracking-tight">No block running right now</div>
        <p className="text-sm text-muted mt-2">Start the next moment when you&apos;re ready — or wait for its scheduled time.</p>
      </div>
    );
  }

  return (
    <div
      data-testid="live-now-card"
      className={`border rounded-sm p-6 bg-surface shadow-sm ${isDelayed ? "border-amber-400" : "border-[#0A0A0A]"}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="font-mono text-[10px] tracking-[0.2em] uppercase mb-1 flex items-center gap-2 text-[#EF4444]">
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#EF4444] live-pulse" />
            LIVE · MOD.LIVE / RUNNING
          </div>
          <div className="font-display text-3xl font-bold text-app tracking-tight leading-none mt-1" data-testid="live-now-title">{block.title}</div>
          <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted font-mono tabular-nums">
            <span className="flex items-center gap-1.5"><Clock size={11} strokeWidth={1.5}/> {humanTime(block.time)} · PLANNED {formatDuration(plannedMin)}</span>
            {block.location && <span className="flex items-center gap-1.5"><MapPin size={11} strokeWidth={1.5}/> {block.location}</span>}
          </div>
        </div>
        {started ? (
          <div className="text-right">
            <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-app">ELAPSED</div>
            <div className="font-mono text-3xl font-semibold tabular-nums text-app leading-none mt-1" data-testid="live-now-elapsed">
              {formatDuration(elapsedMin)}
            </div>
            {overBy > 0 && (
              <div className="mt-1 text-xs text-amber-700 flex items-center gap-1 justify-end font-mono">
                <AlertTriangle size={11} strokeWidth={1.5}/> +{formatDuration(overBy)} OVER
              </div>
            )}
          </div>
        ) : (
          startDelay > 5 && (
            <div className="text-right">
              <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-amber-700">DELAYED</div>
              <div className="font-mono text-3xl font-semibold tabular-nums text-amber-700 leading-none mt-1">{formatDuration(startDelay)}</div>
            </div>
          )
        )}
      </div>

      {canEdit && (
        <div className="mt-5 flex flex-wrap gap-2">
          {!started ? (
            <Button
              onClick={() => onAction(block.id, "start")}
              data-testid={`live-start-${block.id}`}
              className="bg-app hover:bg-[#000000] rounded-sm text-xs h-9"
            >
              <Play size={12} strokeWidth={1.5} className="mr-1.5"/> Start now
            </Button>
          ) : (
            <>
              <Button
                onClick={() => onAction(block.id, "complete")}
                data-testid={`live-complete-${block.id}`}
                className="bg-app hover:bg-[#000000] rounded-sm text-xs h-9"
              >
                <CheckCircle2 size={12} strokeWidth={1.5} className="mr-1.5"/> Mark complete
              </Button>
              {onRewind && (
                <Button
                  onClick={() => {
                    setRewindOpen(v => !v);
                    if (!rewindMsg) setRewindMsg(`Pausing "${block.title}" — restarting shortly.`);
                  }}
                  variant="outline"
                  data-testid={`live-rewind-toggle-${block.id}`}
                  className="rounded-sm text-xs h-9 border-amber-400 text-amber-800 hover:bg-amber-50"
                >
                  <Undo2 size={12} strokeWidth={1.5} className="mr-1.5"/> Rewind
                </Button>
              )}
              <Button
                onClick={() => onAction(block.id, "reset")}
                variant="outline"
                className="rounded-sm text-xs h-9"
              >
                <RotateCcw size={12} strokeWidth={1.5} className="mr-1.5"/> Reset
              </Button>
            </>
          )}
          <Button
            onClick={() => setShiftOpen(v => !v)}
            variant="outline"
            data-testid="live-shift-rest-toggle"
            className={`rounded-sm text-xs h-9 ${isDelayed ? "border-amber-400 text-amber-800 hover:bg-amber-50" : ""}`}
          >
            <FastForward size={12} strokeWidth={1.5} className="mr-1.5"/>
            {isDelayed ? `Shift rest by ${suggestedDelta}m` : "Shift rest…"}
          </Button>
        </div>
      )}

      {canEdit && shiftOpen && onShift && (
        <div
          data-testid="live-shift-panel"
          className="mt-4 border border-hair rounded-sm p-4 bg-[#F3EFE7]/60 space-y-3"
        >
          <div className="eyebrow flex items-center gap-2 text-app">
            <FastForward size={11} strokeWidth={1.5}/> Delay ripple — shift this and everything after
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <Input
                type="number"
                data-testid="live-shift-delta"
                value={shiftDelta}
                onChange={e => setShiftDelta(Number(e.target.value) || 0)}
                className="h-9 w-24 text-sm rounded-sm"
                step={5}
              />
              <span className="text-xs text-muted">minutes (+later / −earlier)</span>
            </div>
            <div className="flex items-center gap-1 text-xs">
              {[5, 10, 15, 30].map(v => (
                <button
                  key={v}
                  type="button"
                  onClick={() => setShiftDelta(v)}
                  data-testid={`live-shift-preset-${v}`}
                  className="px-2 py-1 rounded-sm border border-hair hover:border-[#0A0A0A] text-muted hover:text-app"
                >+{v}m</button>
              ))}
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm text-app">
            <Checkbox
              checked={broadcast}
              onCheckedChange={v => setBroadcast(!!v)}
              data-testid="live-shift-broadcast"
            />
            Also broadcast an update
          </label>

          <label className="flex items-center gap-2 text-sm text-app">
            <Checkbox
              checked={shiftIncludeAnchor}
              onCheckedChange={v => setShiftIncludeAnchor(!!v)}
              data-testid="live-shift-include-anchor"
            />
            <span>
              Shift this block too
              <span className="block text-[11px] text-muted">
                Turn off to keep &ldquo;{block.title}&rdquo; where it is and only push everything strictly after it.
              </span>
            </span>
          </label>

          {broadcast && (
            <div className="space-y-2">
              <Textarea
                data-testid="live-shift-message"
                value={shiftMsg}
                onChange={e => { setShiftMsg(e.target.value); setShiftMsgTouched(true); }}
                rows={2}
                className="text-sm"
              />
              <Select value={shiftAudience} onValueChange={setShiftAudience}>
                <SelectTrigger data-testid="live-shift-audience" className="h-9 rounded-sm text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Everyone</SelectItem>
                  <SelectItem value="vendors">Vendors only</SelectItem>
                  <SelectItem value="clients">Clients only</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <Button
              variant="ghost"
              onClick={() => setShiftOpen(false)}
              className="rounded-sm text-xs h-9"
            >Cancel</Button>
            <Button
              data-testid="live-shift-apply"
              disabled={applying || shiftDelta === 0}
              onClick={async () => {
                setApplying(true);
                try {
                  await onShift({
                    blockId: block.id,
                    deltaMin: Number(shiftDelta) || 0,
                    broadcast: {
                      message: broadcast ? shiftMsg : null,
                      audience: shiftAudience,
                      includeAnchor: shiftIncludeAnchor,
                    },
                  });
                  setShiftOpen(false);
                  setShiftMsg("");
                  setShiftMsgTouched(false);
                } catch (e) { console.error("shift apply failed:", e); }
                finally { setApplying(false); }
              }}
              className="bg-app hover:bg-[#000000] rounded-sm text-xs h-9"
            >
              {applying ? "Shifting…" : `Shift ${shiftDelta > 0 ? "+" : ""}${shiftDelta}m`}
            </Button>
          </div>
        </div>
      )}

      {canEdit && rewindOpen && onRewind && started && (
        <div
          data-testid="live-rewind-panel"
          className="mt-4 border border-amber-300 rounded-sm p-4 bg-amber-50/60 space-y-3"
        >
          <div className="eyebrow flex items-center gap-2 text-amber-800">
            <Undo2 size={11} strokeWidth={1.5}/> Rewind &mdash; undo the start
          </div>
          <p className="text-xs text-muted">
            Clears the &ldquo;started&rdquo; timestamp for &ldquo;{block.title}&rdquo;. Use when the moment kicked off wrong &mdash;
            wrong track, wrong toast, missing person &mdash; and needs a clean restart.
          </p>

          <label className="flex items-center gap-2 text-sm text-app">
            <Checkbox
              checked={rewindBroadcast}
              onCheckedChange={v => setRewindBroadcast(!!v)}
              data-testid="live-rewind-broadcast"
            />
            Broadcast a pause message
          </label>

          {rewindBroadcast && (
            <div className="space-y-2">
              <Textarea
                data-testid="live-rewind-message"
                value={rewindMsg}
                onChange={e => setRewindMsg(e.target.value)}
                rows={2}
                className="text-sm"
              />
              <Select value={rewindAudience} onValueChange={setRewindAudience}>
                <SelectTrigger data-testid="live-rewind-audience" className="h-9 rounded-sm text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Everyone</SelectItem>
                  <SelectItem value="vendors">Vendors only</SelectItem>
                  <SelectItem value="clients">Clients only</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <Button
              variant="ghost"
              onClick={() => setRewindOpen(false)}
              className="rounded-sm text-xs h-9"
            >Cancel</Button>
            <Button
              data-testid={`live-rewind-apply-${block.id}`}
              disabled={rewinding || (rewindBroadcast && !rewindMsg.trim())}
              onClick={async () => {
                setRewinding(true);
                try {
                  await onRewind({
                    blockId: block.id,
                    message: rewindBroadcast ? rewindMsg : null,
                    audience: rewindAudience,
                  });
                  setRewindOpen(false);
                } catch (e) { console.error("rewind apply failed:", e); }
                finally { setRewinding(false); }
              }}
              className="bg-amber-600 hover:bg-amber-700 text-white rounded-sm text-xs h-9"
            >
              {rewinding ? "Rewinding…" : "Confirm rewind"}
            </Button>
          </div>
        </div>
      )}

      {canEdit && (
        <div className="mt-4">
          <div className="eyebrow mb-1.5 flex items-center gap-2"><MessageSquare size={11} strokeWidth={1.5}/> On-the-fly note</div>
          <Textarea
            data-testid="live-now-note"
            value={note}
            onChange={e => setNote(e.target.value)}
            onBlur={() => note !== (block.live_note || "") && onNote(block.id, note)}
            placeholder="Anything to remember — e.g. groom is grabbing cufflinks, bar switched to Prosecco."
            rows={2}
            className="text-sm"
          />
        </div>
      )}
      {!canEdit && block.live_note && (
        <p className="mt-4 text-sm text-muted font-light italic border-l-2 border-hair pl-3">&ldquo;{block.live_note}&rdquo;</p>
      )}
    </div>
  );
}

function UpcomingRow({ block, canEdit, onStart }) {
  return (
    <div className="flex items-center gap-4 p-3 border border-hair rounded-sm bg-surface" data-testid={`live-upcoming-${block.id}`}>
      <div className="w-16 text-xs text-muted tracking-wide">{humanTime(block.time)}</div>
      <div className="flex-1 min-w-0">
        <div className="font-serif text-lg text-app truncate">{block.title}</div>
        <div className="text-xs text-muted">{formatDuration(block.duration_min || 0)}{block.location ? ` · ${block.location}` : ""}</div>
      </div>
      {canEdit && (
        <Button
          onClick={onStart}
          size="sm"
          variant="outline"
          data-testid={`live-start-btn-${block.id}`}
          className="rounded-sm text-xs h-8"
        >
          <Play size={11} strokeWidth={1.5} className="mr-1"/> Start
        </Button>
      )}
    </div>
  );
}

function CompletedRow({ block, canEdit, onReset }) {
  return (
    <div className="flex items-center gap-4 p-3 border border-hair rounded-sm bg-[#F3EFE7] opacity-80">
      <CheckCircle2 size={16} strokeWidth={1.5} className="text-app" />
      <div className="flex-1 min-w-0">
        <div className="text-sm text-app line-through truncate">{block.title}</div>
        <div className="text-xs text-muted">{humanTime(block.time)} · Done {relativeTime(block.completed_at)}</div>
      </div>
      {canEdit && (
        <button
          onClick={onReset}
          className="text-xs text-muted hover:text-app flex items-center gap-1"
        >
          <RotateCcw size={11} strokeWidth={1.5}/> Reset
        </button>
      )}
    </div>
  );
}

function audienceLabel(u) {
  const a = u.audience || "all";
  if (a === "all") return "Everyone";
  if (a === "vendors") return "Vendors";
  if (a === "clients") return "Clients";
  if (a.startsWith("vendor:")) return "A vendor";
  return a;
}

function UpdateCard({ update, canDelete, onDelete }) {
  const isVendorStatus = update.kind === "vendor_status";
  return (
    <div
      data-testid={`update-item-${update.id}`}
      className={`border rounded-sm p-3 ${isVendorStatus ? "border-amber-200 bg-amber-50/50" : "border-hair bg-surface"}`}
    >
      <div className="flex items-center gap-2 text-xs text-muted">
        <span className="font-medium text-app">{update.author_name || "Someone"}</span>
        {isVendorStatus && (
          <Badge variant="outline" className="text-[9px] rounded-sm border-amber-400 text-amber-700 uppercase tracking-widest">Vendor</Badge>
        )}
        <span>·</span>
        <span>{audienceLabel(update)}</span>
        <span>·</span>
        <span>{relativeTime(update.created_at)}</span>
        {canDelete && (
          <button
            onClick={onDelete}
            className="ml-auto text-muted hover:text-red-600"
            title="Delete update"
            data-testid={`update-delete-${update.id}`}
          >
            <Trash2 size={12} strokeWidth={1.5} />
          </button>
        )}
      </div>
      <p className="mt-1.5 text-sm text-app whitespace-pre-wrap leading-relaxed">{update.message}</p>
    </div>
  );
}


/* --- Audit Trail Modal --- */

const ACTION_LABEL = {
  "timeline.start": "started",
  "timeline.complete": "marked complete",
  "timeline.reset": "reset",
  "timeline.rewind": "rewound",
  "timeline.note": "left a note on",
  "timeline.shift": "shifted the timeline from",
  "update.post": "posted an update",
  "update.delete": "deleted an update",
};

function relTime(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  const diff = Math.round((Date.now() - then) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return new Date(iso).toLocaleString();
}

function AuditModal({ open, loading, entries, onClose, onRefresh }) {
  if (!open) return null;
  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="audit-modal"
    >
      <div
        className="bg-surface rounded-sm shadow-2xl max-w-2xl w-full max-h-[85vh] flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-5 border-b border-hair">
          <div>
            <div className="eyebrow flex items-center gap-2"><History size={12} strokeWidth={1.5}/> Activity log</div>
            <div className="font-serif text-2xl text-app mt-1">Post-event review</div>
            <p className="text-sm text-muted font-light mt-0.5">
              Every start, complete, reset, rewind, timeline shift, and update — captured with who and when.
            </p>
          </div>
          <button
            onClick={onClose}
            data-testid="audit-close"
            className="text-muted hover:text-app p-1"
            aria-label="Close activity log"
          >
            <X size={18} strokeWidth={1.5}/>
          </button>
        </div>

        <div className="flex items-center gap-2 px-5 py-2 border-b border-hair/60">
          <span className="text-xs text-muted">
            {loading ? "Loading…" : entries ? `${entries.length} entries` : ""}
          </span>
          <button
            onClick={onRefresh}
            data-testid="audit-refresh"
            className="ml-auto text-xs text-muted hover:text-app inline-flex items-center gap-1"
          >
            <RotateCcw size={10} strokeWidth={1.5}/> Refresh
          </button>
        </div>

        <div className="overflow-y-auto flex-1 p-5" data-testid="audit-list">
          {loading && !entries ? (
            <div className="text-sm text-muted">Loading activity…</div>
          ) : !entries || entries.length === 0 ? (
            <div className="text-sm text-muted font-light">Nothing yet. Activity will appear here as the event day unfolds.</div>
          ) : (
            <ol className="space-y-3">
              {entries.map(e => (
                <li
                  key={e.id}
                  data-testid={`audit-entry-${e.id}`}
                  className="flex items-start gap-3 pb-3 border-b border-hair/60 last:border-b-0"
                >
                  <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-app flex-none" aria-hidden />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-app">
                      <span className="font-medium">{e.actor_name || "Someone"}</span>
                      <span className="text-muted"> {ACTION_LABEL[e.action] || e.action}</span>
                      {e.details?.title && (
                        <span className="text-app"> &ldquo;{e.details.title}&rdquo;</span>
                      )}
                      {e.action === "timeline.shift" && e.details?.delta_min !== undefined && (
                        <span className="text-muted">
                          {" "}by {e.details.delta_min > 0 ? "+" : ""}{e.details.delta_min}m
                          {" "}({e.details.shifted_count} block{e.details.shifted_count === 1 ? "" : "s"})
                        </span>
                      )}
                      {e.action === "update.post" && e.details?.preview && (
                        <span className="text-muted"> &mdash; &ldquo;{e.details.preview}&rdquo;</span>
                      )}
                    </div>
                    <div className="text-[11px] text-muted mt-0.5 tabular-nums">
                      {relTime(e.created_at)} &middot; {new Date(e.created_at).toLocaleString()}
                    </div>
                  </div>
                </li>
              ))}
            </ol>
          )}
        </div>
      </div>
    </div>
  );
}



// ---- Setup & Strike progress widget (Live tab) ----
function SetupProgressWidget({ eventId }) {
  const [summary, setSummary] = React.useState(null);

  React.useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await api.get(`/events/${eventId}/setup-summary`);
        if (!cancelled) setSummary(data);
      } catch (e) { console.error("setup summary load failed:", e); }
    };
    load();
    const iv = setInterval(load, 30000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [eventId]);

  if (!summary || summary.total === 0) return null;

  const pct = (n) => summary.total > 0 ? Math.round((n / summary.total) * 100) : 0;

  return (
    <div className="bevel border-hair rounded-sm p-4" data-testid="setup-progress-widget">
      <div className="flex items-center gap-2 mb-3">
        <ListChecks size={13} strokeWidth={1.5} className="text-app" />
        <div className="eyebrow">Setup progress</div>
        <div className="ml-auto text-[11px] text-muted">{summary.installed}/{summary.total} installed</div>
      </div>
      <div className="space-y-2">
        <ProgressBar label="Arrived" value={summary.arrived} total={summary.total} testid="setup-arrived-bar" />
        <ProgressBar label="Installed" value={summary.installed} total={summary.total} testid="setup-installed-bar" accent />
        {summary.packed > 0 && (
          <ProgressBar label="Packed" value={summary.packed} total={summary.total} testid="setup-packed-bar" />
        )}
        {summary.returned > 0 && (
          <ProgressBar label="Returned" value={summary.returned} total={summary.total} testid="setup-returned-bar" accent />
        )}
      </div>
      <div className="text-[10px] text-muted mt-2">
        {pct(summary.installed)}% install · {pct(summary.returned)}% strike
      </div>
    </div>
  );
}

function ProgressBar({ label, value, total, accent, testid }) {
  const pct = total > 0 ? Math.min(100, Math.round((value / total) * 100)) : 0;
  return (
    <div data-testid={testid}>
      <div className="flex items-center justify-between text-[10px] text-muted mb-0.5">
        <span>{label}</span>
        <span className="tabular-nums">{value}/{total}</span>
      </div>
      <div className="h-1.5 rounded-sm bg-surface-3 overflow-hidden">
        <div
          className={`h-full transition-all ${accent ? "bg-app" : "bg-[#F59E0B]"}`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

